"use client";

import React from "react";
import { motion } from "framer-motion";
import { House as Home, Building2, FileCheck2, Landmark, Layers, ArrowRight, Compass, CircleCheck as CheckCircle2, Bath, UtensilsCrossed, BedDouble, Scale } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const easeCurve = [0.16, 1, 0.3, 1] as const;
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: easeCurve
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05
    }
  }
};
export default function PlanningGuide() {
  return <section id="homeownerplanningguide" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden isolate">
      {/* Background Architectural Grid Texture */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 opacity-30 bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} variants={fadeInUp} className="max-w-3xl mx-auto text-center mb-16 md:mb-24">
          <div className="flex justify-center mb-4">
            <Badge variant="secondary" className="px-3 py-1 font-medium text-xs tracking-wide uppercase">
              Planning &amp; Architectural Context
            </Badge>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground mb-6 leading-tight">
            What to Know Before Planning Your Decatur Renovation
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Every residential renovation begins with clear information. Decatur features a unique intersection of 
            early-twentieth-century bungalows, mid-century brick ranches, and layered local municipal jurisdictions. 
            Understanding the structural characteristics of your home, permitting protocols, and historic preservation 
            frameworks helps clarify the road ahead before formal architectural planning begins.
          </p>
        </motion.div>

        {/* Section 1: Decatur's Housing Stock */}
        <div className="mb-20 md:mb-28">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-60px"
        }} variants={fadeInUp} className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center size-8 rounded-full bg-secondary text-secondary-foreground text-sm font-semibold">
                1
              </span>
              <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground">
                Decatur&apos;s Distinct Housing Stock
              </h3>
            </div>
            <p className="text-muted-foreground text-base max-w-3xl mt-2 leading-relaxed">
              Renovating in Decatur requires an understanding of the structural systems, original materials, and 
              framing methods characteristic of specific construction eras. The three primary residential architectural forms each present distinct considerations during renovation.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-60px"
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Craftsman Bungalows */}
            <motion.div variants={fadeInUp} data-index={0}>
              <Card className="h-full border border-border bg-card/90 backdrop-blur-sm shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-4">
                  <div className="size-11 rounded-lg bg-secondary flex items-center justify-center mb-3 text-foreground">
                    <Home className="size-5 text-chart-1" />
                  </div>
                  <CardTitle className="text-xl font-serif font-semibold">
                    Craftsman Bungalows
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                  <p>
                    Predominantly constructed between 1910 and 1935, Decatur bungalows often feature low-pitched gable roofs, 
                    substantial front porches supported by tapered battered columns, and deep eaves with exposed rafter tails.
                  </p>
                  <div className="pt-2">
                    <span className="font-medium text-foreground block mb-1">Key Structural Realities:</span>
                    <ul className="space-y-1.5 list-disc list-inside text-xs text-muted-foreground">
                      <li>true 2x4 framing often requires sistering or headers for load redistribution.</li>
                      <li>Original crawlspace ventilation and pier foundations require level and moisture assessment.</li>
                      <li>Rear additions must be scaled to avoid overwhelming the historic front roof profile.</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* American Craftsman & Foursquares */}
            <motion.div variants={fadeInUp} data-index={1}>
              <Card className="h-full border border-border bg-card/90 backdrop-blur-sm shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-4">
                  <div className="size-11 rounded-lg bg-secondary flex items-center justify-center mb-3 text-foreground">
                    <Landmark className="size-5 text-chart-1" />
                  </div>
                  <CardTitle className="text-xl font-serif font-semibold">
                    Two-Story Craftsman &amp; Foursquares
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                  <p>
                    Found throughout central neighborhoods, these homes feature symmetrical massing, central staircases, 
                    and compartmentalized floor layouts with substantial original interior millwork, hardwood flooring, and pocket doors.
                  </p>
                  <div className="pt-2">
                    <span className="font-medium text-foreground block mb-1">Key Structural Realities:</span>
                    <ul className="space-y-1.5 list-disc list-inside text-xs text-muted-foreground">
                      <li>Second-floor loads sit on interior bearing walls, requiring steel or LVL beams when opening spaces.</li>
                      <li>Existing gravity heating chases and older plumbing stacks dictate vertical utility runs.</li>
                      <li>Preserving historic window trim and cased openings while improving insulation and air sealing.</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Mid-Century Brick Ranches */}
            <motion.div variants={fadeInUp} data-index={2}>
              <Card className="h-full border border-border bg-card/90 backdrop-blur-sm shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-4">
                  <div className="size-11 rounded-lg bg-secondary flex items-center justify-center mb-3 text-foreground">
                    <Building2 className="size-5 text-chart-1" />
                  </div>
                  <CardTitle className="text-xl font-serif font-semibold">
                    Mid-Century Brick Ranches
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                  <p>
                    Built from the late 1940s through the 1960s, these single-story properties feature low-slung footprints, 
                    exterior masonry brick veneer, and linear room arrangements connecting to walk-out basements or concrete slabs.
                  </p>
                  <div className="pt-2">
                    <span className="font-medium text-foreground block mb-1">Key Structural Realities:</span>
                    <ul className="space-y-1.5 list-disc list-inside text-xs text-muted-foreground">
                      <li>Non-load-bearing partition walls make open-concept kitchen and living reconfiguration straightforward.</li>
                      <li>Lower ceiling heights and low-pitch roof trusses require careful HVAC duct and recessed lighting planning.</li>
                      <li>Basement waterproofing and foundation slab integration are key when capturing lower-level living area.</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>

        {/* Section 2: Permitting Differences (City of Decatur vs Unincorporated DeKalb) */}
        <div className="mb-20 md:mb-28">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-60px"
        }} variants={fadeInUp} className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center size-8 rounded-full bg-secondary text-secondary-foreground text-sm font-semibold">
                2
              </span>
              <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground">
                Jurisdictional Permitting: City of Decatur vs. Unincorporated DeKalb County
              </h3>
            </div>
            <p className="text-muted-foreground text-base max-w-3xl mt-2 leading-relaxed">
              Properties with a Decatur postal address fall into one of two distinct building and planning jurisdictions. 
              The location of your property line dictates the governing code enforcement agency, tree canopy requirements, 
              stormwater retention thresholds, and plan review workflow.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-60px"
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* City of Decatur */}
            <motion.div variants={fadeInUp} data-index={0}>
              <div className="border border-border rounded-xl p-6 sm:p-8 bg-card shadow-sm h-full flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between gap-4 mb-4">
                    <h4 className="font-serif text-xl font-semibold text-foreground">
                      City of Decatur (Municipal Limits)
                    </h4>
                    <Badge variant="outline" className="text-xs">Municipal Authority</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                    Operated under the City of Decatur Design, Environment &amp; Construction Division. The city maintains 
                    a unified Unified Development Ordinance (UDO) with specific focus on neighborhood character, stormwater, and urban tree canopy.
                  </p>
                  <Separator className="my-4" />
                  <dl className="space-y-4 text-sm">
                    <div>
                      <dt className="font-medium text-foreground flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-chart-1" />
                        Tree Canopy Ordinance
                      </dt>
                      <dd className="text-muted-foreground text-xs pl-6 mt-1">
                        Strict tree canopy coverage rules require tree protection fencing, tree impact assessments, and mitigation calculations for boundary trees before ground disturbance.
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-foreground flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-chart-1" />
                        Impervious Surface &amp; Floor Area Ratio (FAR)
                      </dt>
                      <dd className="text-muted-foreground text-xs pl-6 mt-1">
                        Strict lot coverage limits (impervious surface ratios) and FAR formulas govern allowable square footage expansions, driveway additions, and detached structures.
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-foreground flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-chart-1" />
                        Plan Review &amp; Inspection Path
                      </dt>
                      <dd className="text-muted-foreground text-xs pl-6 mt-1">
                        Integrated municipal plan review with dedicated city building inspectors. Typical initial residential plan review cycles run approximately 2 to 4 weeks.
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </motion.div>

            {/* Unincorporated DeKalb County */}
            <motion.div variants={fadeInUp} data-index={1}>
              <div className="border border-border rounded-xl p-6 sm:p-8 bg-card shadow-sm h-full flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between gap-4 mb-4">
                    <h4 className="font-serif text-xl font-semibold text-foreground">
                      Unincorporated DeKalb County
                    </h4>
                    <Badge variant="outline" className="text-xs">County Authority</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                    Administered through the DeKalb County Department of Planning &amp; Sustainability. Applies to residential 
                    addresses in areas such as Medlock Park, North Decatur, Valley Brook, and Laurel Ridge.
                  </p>
                  <Separator className="my-4" />
                  <dl className="space-y-4 text-sm">
                    <div>
                      <dt className="font-medium text-foreground flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-chart-1" />
                        County Zoning &amp; Setback Verification
                      </dt>
                      <dd className="text-muted-foreground text-xs pl-6 mt-1">
                        Zoning classifications (e.g., R-75, R-85, R-100) specify front, side, and rear yard setbacks. Additions must conform to boundary lines without encroaching on easements.
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-foreground flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-chart-1" />
                        Stormwater &amp; Land Disturbance
                      </dt>
                      <dd className="text-muted-foreground text-xs pl-6 mt-1">
                        Land disturbance permits (LDP) are triggered when total soil disturbance exceeds established county thresholds, requiring civil silt fencing and erosion control plans.
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-foreground flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-chart-1" />
                        Online Review Portal Workflow
                      </dt>
                      <dd className="text-muted-foreground text-xs pl-6 mt-1">
                        Processed electronically through the county&apos;s digital portal. Plan routing involves structural, trade, and zoning reviews prior to permit issuance.
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Section 3: Historic District Design Review */}
        <div className="mb-20 md:mb-28">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-60px"
        }} variants={fadeInUp} className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center size-8 rounded-full bg-secondary text-secondary-foreground text-sm font-semibold">
                3
              </span>
              <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground">
                Historic District Design Review for Exterior Work
              </h3>
            </div>
            <p className="text-muted-foreground text-base max-w-3xl mt-2 leading-relaxed">
              If your residence is located within a locally designated historic district in the City of Decatur 
              (such as the McDonough-Adams-Kings Highway Local Historic District, Old Decatur, or Clairemont Avenue), 
              exterior alterations are subject to design review before building permits are issued.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-60px"
        }} variants={fadeInUp}>
            <Card className="border border-border bg-card/85 backdrop-blur-md p-6 sm:p-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-4">
                  <div className="size-12 rounded-lg bg-secondary flex items-center justify-center text-foreground">
                    <FileCheck2 className="size-6 text-chart-1" />
                  </div>
                  <h4 className="font-serif text-xl font-semibold text-foreground">
                    The Certificate of Appropriateness (COA)
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    A Certificate of Appropriateness is a formal authorization issued by the Decatur Historic Preservation Commission (HPC) 
                    or administrative planning staff verifying that proposed exterior alterations adhere to established district guidelines.
                  </p>
                  <div className="p-3 bg-secondary/60 rounded-lg text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground block mb-0.5">Note on Interior Work:</span>
                    Interior renovations, plumbing updates, kitchen modernizations, and basement buildouts do not require HPC review unless they alter exterior window openings or rooflines.
                  </div>
                </div>

                <div className="lg:col-span-2 space-y-6">
                  <h5 className="text-sm font-semibold tracking-wide uppercase text-foreground border-b border-border pb-2">
                    Primary Exterior Design Review Standards
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 font-medium text-foreground">
                        <Layers className="size-4 text-chart-1" />
                        Window &amp; Siding Materials
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Original wood lap siding, cedar shakes, and brick masonry should be retained where feasible. 
                        Replacement windows visible from public rights-of-way must match the original lite patterns, muntin profiles, and material appearance.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 font-medium text-foreground">
                        <Compass className="size-4 text-chart-1" />
                        Massing &amp; Height Transitions
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Second-story additions and rear expansions must respect the scale and roofline of the original structure. 
                        Additions are generally required to be stepped in from side elevations or stepped down in ridge height to maintain secondary visual prominence.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 font-medium text-foreground">
                        <Home className="size-4 text-chart-1" />
                        Front Porches &amp; Entryways
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Porches are defining architectural features in Decatur&apos;s historic neighborhoods. 
                        Enclosing front porches or altering original column shapes, balustrades, or porch floor materials is generally discouraged under district standards.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 font-medium text-foreground">
                        <Scale className="size-4 text-chart-1" />
                        Public Streetscape Visibility
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Guidelines apply to work visible from the public street. Rear additions with minimal street visibility have greater latitude regarding modern materials and fenestration styles.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Section 4: Common Catalysts for Reaching Out */}
        <div className="mb-16 md:mb-20">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-60px"
        }} variants={fadeInUp} className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center size-8 rounded-full bg-secondary text-secondary-foreground text-sm font-semibold">
                4
              </span>
              <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground">
                Common Moments That Prompt a Renovation Project
              </h3>
            </div>
            <p className="text-muted-foreground text-base max-w-3xl mt-2 leading-relaxed">
              Homeowners typically initiate conversations with remodeling professionals during specific transitional 
              moments when their current home layout no longer accommodates their daily routines.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-60px"
        }} variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Catalyst 1 */}
            <motion.div variants={fadeInUp} data-index={0}>
              <Card className="h-full border border-border bg-card p-5 flex flex-col justify-between shadow-xs">
                <div>
                  <div className="size-10 rounded-md bg-secondary flex items-center justify-center mb-4 text-foreground">
                    <BedDouble className="size-5 text-chart-1" />
                  </div>
                  <h4 className="font-serif text-lg font-semibold mb-2 text-foreground">
                    Needing an Additional Bedroom or Suite
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Growing families, multigenerational living, or the permanent need for quiet home office space often requires 
                    reconfiguring existing square footage, converting an attic, or engineering a primary suite addition.
                  </p>
                </div>
              </Card>
            </motion.div>

            {/* Catalyst 2 */}
            <motion.div variants={fadeInUp} data-index={1}>
              <Card className="h-full border border-border bg-card p-5 flex flex-col justify-between shadow-xs">
                <div>
                  <div className="size-10 rounded-md bg-secondary flex items-center justify-center mb-4 text-foreground">
                    <UtensilsCrossed className="size-5 text-chart-1" />
                  </div>
                  <h4 className="font-serif text-lg font-semibold mb-2 text-foreground">
                    A Cramped or Segmented Kitchen
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Older Decatur homes often isolate the kitchen behind narrow doorways. Expanding sightlines, integrating 
                    functional pantries, and creating continuous counter work zones improve circulation for cooking and gatherings.
                  </p>
                </div>
              </Card>
            </motion.div>

            {/* Catalyst 3 */}
            <motion.div variants={fadeInUp} data-index={2}>
              <Card className="h-full border border-border bg-card p-5 flex flex-col justify-between shadow-xs">
                <div>
                  <div className="size-10 rounded-md bg-secondary flex items-center justify-center mb-4 text-foreground">
                    <Bath className="size-5 text-chart-1" />
                  </div>
                  <h4 className="font-serif text-lg font-semibold mb-2 text-foreground">
                    Outdated Bathroom Infrastructure
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Aging cast-iron drain lines, failing tile waterproofing, inadequate vanity storage, or a single shared bathroom 
                    for three bedrooms frequently prompt comprehensive bathroom reconfigurations and additions.
                  </p>
                </div>
              </Card>
            </motion.div>

            {/* Catalyst 4 */}
            <motion.div variants={fadeInUp} data-index={3}>
              <Card className="h-full border border-border bg-card p-5 flex flex-col justify-between shadow-xs">
                <div>
                  <div className="size-10 rounded-md bg-secondary flex items-center justify-center mb-4 text-foreground">
                    <Scale className="size-5 text-chart-1" />
                  </div>
                  <h4 className="font-serif text-lg font-semibold mb-2 text-foreground">
                    Weighing Remodeling vs. Relocating
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Homeowners who love their neighborhood, tree-lined streets, and Decatur school access often evaluate the high transactional 
                    costs and interest rates of moving against investing in a tailored whole-home renovation.
                  </p>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        </div>

        {/* Informational Closing / Next Steps Card */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-60px"
      }} variants={fadeInUp} className="rounded-2xl border border-border bg-secondary/40 p-8 sm:p-10 text-center max-w-3xl mx-auto">
          <h3 className="font-serif text-2xl font-semibold text-foreground mb-3">
            Ready to Explore Your Project Possibilities?
          </h3>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
            Reviewing your home&apos;s existing floor plan, structural boundaries, and zoning parameters is the first step 
            in turning ideas into achievable architectural plans.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/ProcessPlanningPage" className="inline-flex items-center gap-2">
                Explore the Planning Process
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/ContactPage">
                Connect With Our Team
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}